import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { api } from '../lib/api';
import { Sparkles, Send, MapPin, Plane, Lightbulb, Info, Loader2 } from 'lucide-react';
import { Seo } from '../components/Seo';

const CHIPS = [
  'I have ₹70,000 and 7 days from Kolkata. I want beaches and nightlife.',
  'Budget trip to Southeast Asia for 5 days from Delhi, love street food.',
  'Romantic 4-day getaway from Mumbai under ₹50,000.',
  'Family-friendly 6 days with kids, from Bengaluru, some theme parks.',
];

export default function AITripPlanner() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [isAI, setIsAI] = useState(true);
  const threadRef = useRef(null);

  useEffect(() => { document.title = 'AI Trip Planner | FlightDeals.in'; api.track('ai_opened'); }, []);

  const submit = async (text) => {
    const p = text || prompt;
    if (!p.trim()) return;
    setLoading(true); setPlan(null);
    try {
      const { data } = await api.aiPlan(p);
      setPlan(data.plan); setIsAI(data.is_ai_generated);
      setTimeout(() => threadRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  };

  const searchDest = (code) => {
    api.track('ai_recommendation_clicked', null, { code });
    navigate(`/flights/results?origin=CCU&destination=${code}&trip_type=round_trip&adults=1&cabin=economy`);
  };

  return (
    <Layout>
      <Seo path="/ai-travel-planner" title="AI Trip Planner — Plan a Trip by Budget in Seconds"
        description="Tell us your origin, budget, days and interests. Our AI drafts a day-by-day trip plan with destination ideas you can instantly compare on FlightDeals."
        jsonLd={{ '@context': 'https://schema.org', '@type': 'WebApplication', name: 'FlightDeals.in AI Trip Planner', applicationCategory: 'TravelApplication', operatingSystem: 'Web', offers: { '@type': 'Offer', price: '0', priceCurrency: 'INR' } }} />
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <span className="inline-flex items-center gap-1.5 text-accent text-sm font-semibold"><Sparkles className="h-4 w-4" /> AI Trip Planner</span>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold">Describe your dream trip</h1>
          <p className="mt-2 text-primary-foreground/80">Tell us your origin, budget, days and interests. We’ll suggest destinations and a day-by-day plan — then you compare live deals here.</p>
          <div className="mt-5 rounded-2xl bg-card text-foreground p-3">
            <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
              placeholder="e.g. I have ₹70,000 and 7 days from Kolkata. I want beaches and nightlife."
              className="border-0 focus-visible:ring-0 resize-none text-base" data-testid="ai-planner-prompt-input" />
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-muted-foreground">Powered by AI · inspiration only</p>
              <Button onClick={() => submit()} disabled={loading} className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold" data-testid="ai-planner-send-button">
                {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} Plan my trip
              </Button>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {CHIPS.map((c, i) => (
              <button key={i} onClick={() => { setPrompt(c); submit(c); }} className="text-left text-xs bg-white/10 hover:bg-white/20 border border-white/15 rounded-full px-3 py-1.5" data-testid={`ai-chip-${i}`}>
                <Lightbulb className="h-3 w-3 inline mr-1 text-accent" />{c.length > 46 ? c.slice(0, 46) + '…' : c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" ref={threadRef} data-testid="ai-planner-thread">
        {loading && (
          <div className="flex items-center gap-3 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin text-accent" /> Crafting your itinerary…</div>
        )}
        {plan && (
          <div className="space-y-6">
            <Card className="p-6 rounded-2xl">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="h-4 w-4 text-accent" />
                <span className="text-xs font-semibold uppercase tracking-wide text-accent">{isAI ? 'AI itinerary' : 'Sample itinerary'}</span>
              </div>
              <p className="text-lg">{plan.summary}</p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                {plan.travel_style && <span className="rounded-full bg-secondary px-2.5 py-1">{plan.travel_style}</span>}
                {plan.duration_days && <span className="rounded-full bg-secondary px-2.5 py-1">{plan.duration_days} days</span>}
                {plan.budget && <span className="rounded-full bg-secondary px-2.5 py-1">{plan.budget}</span>}
              </div>
            </Card>

            <div>
              <h3 className="font-display font-semibold text-xl mb-3">Suggested destinations</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                {plan.destinations?.map((d, i) => (
                  <Card key={i} className="p-5 rounded-2xl">
                    <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-accent" /><p className="font-semibold">{d.city}, {d.country}</p></div>
                    <p className="text-sm text-muted-foreground mt-1">{d.why}</p>
                    <p className="text-[11px] text-muted-foreground mt-2 italic">{d.est_budget_note}</p>
                    {d.code && <Button onClick={() => searchDest(d.code)} variant="outline" size="sm" className="mt-3 rounded-xl" data-testid={`ai-search-${d.code}`}><Plane className="h-3.5 w-3.5 mr-1.5" /> Compare flights</Button>}
                  </Card>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-display font-semibold text-xl mb-3">Day-by-day plan</h3>
              <div className="space-y-3">
                {plan.itinerary?.map((day) => (
                  <Card key={day.day} className="p-4 rounded-2xl flex gap-4">
                    <span className="grid place-items-center h-9 w-9 shrink-0 rounded-xl bg-accent/10 text-accent font-semibold tabular">{day.day}</span>
                    <div>
                      <p className="font-semibold text-sm">{day.title}</p>
                      <ul className="mt-1 text-sm text-muted-foreground list-disc pl-4 space-y-0.5">
                        {day.activities?.map((a, i) => <li key={i}>{a}</li>)}
                      </ul>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {plan.tips?.length > 0 && (
              <Card className="p-5 rounded-2xl">
                <p className="font-semibold mb-2">Tips</p>
                <ul className="text-sm text-muted-foreground list-disc pl-4 space-y-1">{plan.tips.map((t, i) => <li key={i}>{t}</li>)}</ul>
              </Card>
            )}

            <div className="rounded-xl border border-border bg-secondary/50 p-4 text-xs text-muted-foreground flex items-start gap-2">
              <Info className="h-4 w-4 mt-0.5 shrink-0 text-accent" /> {plan.disclaimer}
            </div>
          </div>
        )}
        {!loading && !plan && (
          <div className="text-center py-16 text-muted-foreground"><Sparkles className="h-10 w-10 mx-auto mb-3 opacity-40" /><p>Your personalised trip plan will appear here.</p></div>
        )}
      </section>
    </Layout>
  );
}
